import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  LuArrowLeft,
  LuCheck,
  LuChevronDown,
  LuCopy,
  LuGift,
  LuLoader2,
  LuPencil,
  LuUserX,
} from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import { handleMenuKeys } from "../../app-components/header/menuKeys";
import Avatar from "../../app-components/ui/Avatar";
import ConfirmDialog from "../../app-components/ui/ConfirmDialog";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, btnSecondary, card } from "../../app-components/ui/styles";
import { SITELINKS } from "../../app-routes/links";
import useDismiss from "../../hooks/useDismiss";
import { getErrorMessage } from "../../utils";
import notify from "../../utils/notify";
import { fmtMoney } from "../dashboard/components/format";
import GrantSubscriptionDialog from "./components/GrantSubscriptionDialog";
import ProfileStepper, { Step } from "./profile/ProfileStepper";

/* ------------------------------------------------------------------ */
/* types (GET elite-users/{id}/profile)                                */
/* ------------------------------------------------------------------ */

type Member = Record<string, any> & {
  id: number;
  location: { country: string | null; state: string | null; city: string | null };
  interests: string[];
  age: number | null;
  unique_no: string;
};
type Sub = { id: number; subscription_plan: string | null; subscription_plan_duration: number | null; status: string; start_date: string; end_date: string };
type Tx = {
  id: number; transaction_code: string; payment_reference: string; payment_method: string; gateway: string | null;
  amount: string; currency?: string | null; paid_amount?: string | null; status: string; failure_reason?: string | null;
  created_at: string; subscription_plan: string | null;
};
type Profile = {
  member: Member;
  stages: { steps: Step[]; current: number };
  subscriptions: { active: Sub | null; history: Sub[] };
  transactions: Tx[];
  grants: { id: number; plan: string; duration_months: number; ends_at: string; reason: string; created_at: string; admin_name: string | null }[];
  activity: {
    requests: { total: number; by_status?: Record<string, number> };
    responses_given: { total: number };
    elite_requests: { total: number };
    elite_responses_given: { total: number };
    latest_requests: { uuid: string; status: string; created_at: string; interest: string | null }[];
    devices: { device_type: string; last_seen: string }[];
    last_action: { action_name: string; action_date: string } | null;
    notifications: { total: number; unread: number };
  };
  safety: Record<string, number>;
  media: { photos: { id: number; photo_link: string; caption: string | null }[]; gallery_count: number };
  admin_history: { id: number; action: string; summary: string; created_at: string; admin_name: string | null }[];
};

/* ------------------------------------------------------------------ */
/* small helpers                                                       */
/* ------------------------------------------------------------------ */

const day = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "–";
const planName = (p?: string | null) => (p ? (p.toUpperCase() === "ELITEGOLD" ? "Elite Gold" : p.charAt(0) + p.slice(1).toLowerCase()) : "–");
const yes = (v: unknown) => v !== null && v !== undefined && !["", "0", "false", "no"].includes(String(v).toLowerCase());

const txAmount = (t: Tx) => {
  if ((t.payment_reference ?? "").toUpperCase() === "FREE TRIAL") return "Free trial";
  if ((t.payment_method ?? "").toUpperCase() === "COMPLIMENTARY") return "Complimentary";
  const cur = (t.currency ?? "").toUpperCase();
  return cur === "NGN" || cur === "USD" ? fmtMoney(cur, Number(t.amount)) : Number(t.amount).toLocaleString();
};

const Section = ({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) => (
  <section className={`${card} p-5`}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-[15px] font-semibold text-honeConnectBlack">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium text-hcMuted">{label}</dt>
    <dd className="mt-0.5 break-words text-sm text-honeConnectBlack">{value === null || value === undefined || value === "" ? "–" : value}</dd>
  </div>
);

const Stat = ({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) => (
  <div className={`${card} p-4`}>
    <p className="text-xs text-hcMuted">{label}</p>
    <p className="mt-1 text-lg font-semibold text-honeConnectBlack">{value}</p>
    {hint && <p className="mt-0.5 text-xs text-hcMuted">{hint}</p>}
  </div>
);

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "payments", label: "Subscriptions & payments" },
  { key: "activity", label: "Activity" },
  { key: "history", label: "Admin history" },
] as const;
type Tab = (typeof TABS)[number]["key"];

/* ------------------------------------------------------------------ */
/* actions menu (same actions as the Users list)                       */
/* ------------------------------------------------------------------ */

const ActionsMenu = ({ eliteStatus, busy, onApprove, onGrant, onRemove }: {
  eliteStatus: string; busy: boolean; onApprove: () => void; onGrant: () => void; onRemove: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dismiss = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, dismiss);
  const s = eliteStatus.toUpperCase();
  const item = "flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors focus:outline-none";
  const run = (fn: () => void) => { setOpen(false); fn(); };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} disabled={busy} aria-haspopup="menu" aria-expanded={open}
        className={`${btnSecondary} inline-flex items-center gap-2`}>
        {busy ? <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        Actions
        <LuChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <div role="menu" onKeyDown={handleMenuKeys} className="absolute right-0 z-30 mt-1 w-52 overflow-hidden rounded-xl border border-hcBorder bg-white py-1.5 shadow-pop">
          {s !== "APPROVED" && (
            <button type="button" role="menuitem" onClick={() => run(onApprove)} className={`${item} hover:bg-hcBg focus:bg-hcBg`}>
              <LuCheck className="h-4 w-4 text-emerald-600" aria-hidden /> Approve
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => run(onGrant)} className={`${item} hover:bg-hcBg focus:bg-hcBg`}>
            <LuGift className="h-4 w-4 text-honeConnectPurple" aria-hidden /> Grant subscription
          </button>
          {s !== "REMOVED" && (
            <>
              <div className="my-1 border-t border-hcBorder" />
              <button type="button" role="menuitem" onClick={() => run(onRemove)} className={`${item} text-red-600 hover:bg-red-50 focus:bg-red-50`}>
                <LuUserX className="h-4 w-4" aria-hidden /> Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* page                                                                */
/* ------------------------------------------------------------------ */

const ShowUser = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Profile | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<Tab>("overview");
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res: any = await apiClient.get(`elite-users/${id}/profile`);
      setData(res?.data ?? null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const m = data?.member;
  const name = m ? m.linkedin_full_name || m.full_name || m.username || "Member" : "";

  const setStatus = async (status: "APPROVED" | "REMOVED") => {
    setBusy(true);
    try {
      const res: any = await apiClient.patch(`elite-users/${id}/status`, { status });
      const sent: Record<string, boolean> = res?.data?.notifications ?? {};
      const failed = Object.keys(sent).filter((k) => !sent[k]);
      if (status === "APPROVED" && failed.length) notify.warning(`${name} approved, but some notifications didn't go out (${failed.join(", ")}).`);
      else notify.success(status === "APPROVED" ? `${name} approved.` : `${name} removed.`);
      setConfirmRemove(false);
      load();
    } catch (err) {
      notify.error(`Couldn't ${status === "APPROVED" ? "approve" : "remove"} ${name}: ${getErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify.success(`${what} copied.`);
    } catch {
      notify.error("Couldn't copy to the clipboard.");
    }
  };

  // money paid, per currency (never mixed)
  const paid = (data?.transactions ?? []).filter((t) => t.status === "PAID" && Number(t.amount) > 0 && (t.currency === "NGN" || t.currency === "USD"));
  const totalBy = (cur: "NGN" | "USD") => paid.filter((t) => t.currency === cur).reduce((s, t) => s + Number(t.paid_amount ?? t.amount), 0);
  const paidText = [totalBy("NGN") ? fmtMoney("NGN", totalBy("NGN")) : null, totalBy("USD") ? fmtMoney("USD", totalBy("USD")) : null].filter(Boolean).join(" · ") || "Nothing yet";

  return (
    <AdminLayout title={name ? `Admin | ${name}` : "Admin | Member"}>
      <Link to={SITELINKS.ELITE_USER} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-honeConnectGray hover:text-honeConnectPurple">
        <LuArrowLeft className="h-4 w-4" aria-hidden /> Back to Users
      </Link>

      {error && !data ? (
        <div className={`${card} flex flex-col items-center px-6 py-14 text-center`}>
          <p className="mb-1 font-semibold text-honeConnectBlack">Couldn't load this member</p>
          <p className="mb-4 max-w-md text-sm text-hcMuted">{error}</p>
          <button onClick={load} className={btnPrimary}>Try again</button>
        </div>
      ) : !data || !m ? (
        <div className={`${card} flex justify-center py-16`}><LuLoader2 className="h-6 w-6 animate-spin text-hcMuted" aria-hidden /></div>
      ) : (
        <>
          {/* header */}
          <section className={`${card} p-5`}>
            <div className="flex flex-wrap items-start gap-4">
              <Avatar name={name} src={m.linkedin_photo || m.profile_pix_link} size={72} />
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-semibold text-honeConnectBlack">{name}</h1>
                <p className="mt-0.5 text-sm text-hcMuted">
                  {[m.username ? `@${m.username}` : null, `No. ${m.unique_no}`, `Member since ${day(m.created_at)}`].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <StatusPill label={m.elite_status || "Pending"} />
                  {m.status && <StatusPill label={m.status} />}
                  {data.subscriptions.active && <StatusPill tone="purple" label={planName(data.subscriptions.active.subscription_plan)} />}
                  {yes(m.is_private) && <StatusPill tone="neutral" label="Private profile" />}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to={`${SITELINKS.UPDATE_USER}${m.id}`} className={`${btnSecondary} inline-flex items-center gap-2`}>
                  <LuPencil className="h-4 w-4" aria-hidden /> Edit profile
                </Link>
                <ActionsMenu eliteStatus={m.elite_status || ""} busy={busy} onApprove={() => setStatus("APPROVED")} onGrant={() => setGrantOpen(true)} onRemove={() => setConfirmRemove(true)} />
              </div>
            </div>

            <div className="mt-6 overflow-x-auto border-t border-hcBorder pt-5">
              <ProfileStepper steps={data.stages.steps} current={data.stages.current} />
            </div>
          </section>

          {/* key numbers */}
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Subscription" value={data.subscriptions.active ? planName(data.subscriptions.active.subscription_plan) : "None"}
              hint={data.subscriptions.active ? `Ends ${day(data.subscriptions.active.end_date)}` : "No active subscription"} />
            <Stat label="Paid in total" value={<span className="text-base">{paidText}</span>} hint={`${paid.length} payment${paid.length === 1 ? "" : "s"}`} />
            <Stat label="Requests posted" value={data.activity.requests.total} hint={`${data.activity.requests.by_status?.accepted ?? 0} accepted`} />
            <Stat label="Last sign-in" value={<span className="text-base">{day(m.last_login)}</span>} hint={data.activity.devices.map((d) => d.device_type).join(", ") || "No device recorded"} />
          </div>

          {/* tabs */}
          <div className="mt-5 flex gap-1 overflow-x-auto border-b border-hcBorder" role="tablist">
            {TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                className={`-mb-px whitespace-nowrap border-b-2 px-4 pb-3 text-sm font-medium transition ${
                  tab === t.key ? "border-honeConnectPurple text-honeConnectPurple" : "border-transparent text-honeConnectGray hover:text-honeConnectBlack"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-5 space-y-5">
            {tab === "overview" && (
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
                <Section title="Personal details">
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <Field label="Full name" value={m.full_name} />
                    <Field label="LinkedIn name" value={m.linkedin_full_name} />
                    <Field label="Gender" value={m.gender ? m.gender.charAt(0) + m.gender.slice(1).toLowerCase() : null} />
                    <Field label="Date of birth" value={m.DOB ? `${day(m.DOB)}${m.age !== null ? ` (${m.age})` : ""}` : null} />
                  </dl>
                </Section>
                <Section title="Contact & location">
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <Field label="Email" value={<>{m.email}{yes(m.is_email_verified) || m.email_verified_at ? <span className="ml-2 text-xs text-emerald-600">Verified</span> : null}</>} />
                    <Field label="LinkedIn email" value={m.linkedin_email} />
                    <Field label="Phone" value={<>{m.phone_number}{m.phone_number && (yes(m.is_phone_verified) || m.phone_verified_at) ? <span className="ml-2 text-xs text-emerald-600">Verified</span> : null}</>} />
                    <Field label="Location" value={[m.area, m.location.city, m.location.state, m.location.country].filter(Boolean).join(", ")} />
                  </dl>
                </Section>
                <Section title="Account">
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <Field label="Referral code" value={m.referral_code ? (
                      <span className="inline-flex items-center gap-2 font-mono">{m.referral_code}
                        <button type="button" onClick={() => copy(m.referral_code, "Referral code")} aria-label="Copy referral code" className="text-hcMuted hover:text-honeConnectPurple"><LuCopy className="h-3.5 w-3.5" aria-hidden /></button>
                      </span>) : null} />
                    <Field label="Profile visibility" value={yes(m.is_private) ? "Private" : "Public"} />
                    <Field label="Email notifications" value={yes(m.is_email_notification) ? "On" : "Off"} />
                    <Field label="Notifications in app" value={`${data.activity.notifications.total} (${data.activity.notifications.unread} unread)`} />
                  </dl>
                </Section>
                <Section title="Profile">
                  <dl className="space-y-4">
                    <Field label="Bio" value={m.bio ? <span className="whitespace-pre-line">{m.bio}</span> : null} />
                    <Field label="Interests" value={data.member.interests.length ? (
                      <span className="flex flex-wrap gap-1.5">{data.member.interests.map((i) => <span key={i} className="rounded-full bg-hcBg px-2.5 py-0.5 text-xs">{i}</span>)}</span>) : null} />
                  </dl>
                </Section>
                <Section title="Social">
                  <dl className="space-y-4">
                    <Field label="LinkedIn" value={m.linkedin_link ? <a href={m.linkedin_link} target="_blank" rel="noopener noreferrer" className="text-honeConnectPurple hover:underline">Open profile</a> : null} />
                    <Field label="Instagram" value={m.instagram_id} />
                    <Field label="Snapchat" value={m.snapchat_id} />
                    <Field label="X (Twitter)" value={m.twitter_x_id} />
                  </dl>
                </Section>
                <Section title="Safety">
                  <dl className="grid grid-cols-2 gap-4">
                    <Field label="Blocked by this member" value={data.safety.blocked_by_member} />
                    <Field label="Members who blocked them" value={data.safety.blocked_this_member} />
                    <Field label="Blocked emails" value={data.safety.blocked_emails} />
                    <Field label="Blocked phones" value={data.safety.blocked_phones} />
                    <Field label="Excluded contacts" value={data.safety.excluded_contacts} />
                  </dl>
                </Section>
              </div>
            )}

            {tab === "payments" && (
              <>
                <Section title="Payments">
                  {data.transactions.length === 0 ? <p className="text-sm text-hcMuted">No payments yet.</p> : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead><tr className="text-left text-xs uppercase tracking-wide text-hcMuted">
                          {["Date", "Amount", "Plan", "Gateway", "Reference", "Status"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>)}
                        </tr></thead>
                        <tbody>{data.transactions.map((t) => (
                          <tr key={t.id} className="border-t border-hcBorder">
                            <td className="whitespace-nowrap px-3 py-2.5 text-hcMuted">{day(t.created_at)}</td>
                            <td className="whitespace-nowrap px-3 py-2.5 font-medium">{txAmount(t)}</td>
                            <td className="whitespace-nowrap px-3 py-2.5">{planName(t.subscription_plan)}</td>
                            <td className="whitespace-nowrap px-3 py-2.5 capitalize">{t.gateway ?? "–"}</td>
                            <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">{t.payment_reference}</td>
                            <td className="whitespace-nowrap px-3 py-2.5"><StatusPill label={t.status} />{t.failure_reason === "amount_mismatch" && <span className="ml-2 text-xs text-red-600">Amount mismatch</span>}</td>
                          </tr>))}</tbody>
                      </table>
                    </div>)}
                </Section>
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  <Section title="Subscriptions">
                    {data.subscriptions.history.length === 0 ? <p className="text-sm text-hcMuted">No subscriptions yet.</p> : (
                      <ul className="divide-y divide-hcBorder">{data.subscriptions.history.map((s) => {
                        const live = s.status === "ACTIVE" && new Date(s.end_date) > new Date();
                        return (
                          <li key={s.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                            <span><span className="font-medium">{planName(s.subscription_plan)}</span><span className="text-hcMuted"> · {s.subscription_plan_duration ?? "–"} month{s.subscription_plan_duration === 1 ? "" : "s"}</span></span>
                            <span className="flex items-center gap-2 text-xs text-hcMuted">{day(s.start_date)} – {day(s.end_date)}<StatusPill tone={live ? "success" : "neutral"} label={live ? "Active" : s.status === "ACTIVE" ? "Ended" : s.status} /></span>
                          </li>);
                      })}</ul>)}
                  </Section>
                  <Section title="Complimentary grants">
                    {data.grants.length === 0 ? <p className="text-sm text-hcMuted">No grants. Use Actions → Grant subscription.</p> : (
                      <ul className="divide-y divide-hcBorder">{data.grants.map((g) => (
                        <li key={g.id} className="py-2.5 text-sm">
                          <p><span className="font-medium">{planName(g.plan)}</span> · {g.duration_months} month{g.duration_months === 1 ? "" : "s"} <span className="text-hcMuted">until {day(g.ends_at)}</span></p>
                          <p className="text-xs text-hcMuted">{g.admin_name ?? "An admin"} on {day(g.created_at)}: {g.reason}</p>
                        </li>))}</ul>)}
                  </Section>
                </div>
              </>
            )}

            {tab === "activity" && (
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
                <Section title="Requests">
                  <dl className="grid grid-cols-2 gap-4">
                    <Field label="Posted" value={data.activity.requests.total} />
                    <Field label="Responses given" value={data.activity.responses_given.total} />
                    <Field label="Elite requests" value={data.activity.elite_requests.total} />
                    <Field label="Elite responses" value={data.activity.elite_responses_given.total} />
                    {Object.entries(data.activity.requests.by_status ?? {}).map(([k, v]) => <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} value={v} />)}
                  </dl>
                </Section>
                <Section title="Latest requests" action={<Link to={`${SITELINKS.REQUEST}`} className="text-xs font-semibold text-honeConnectPurple hover:underline">All requests</Link>}>
                  {data.activity.latest_requests.length === 0 ? <p className="text-sm text-hcMuted">No requests yet.</p> : (
                    <ul className="divide-y divide-hcBorder">{data.activity.latest_requests.map((r) => (
                      <li key={r.uuid} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                        <span>{r.interest ?? "Request"}<span className="block text-xs text-hcMuted">{day(r.created_at)}</span></span>
                        <StatusPill label={r.status} />
                      </li>))}</ul>)}
                </Section>
                <Section title="Devices & usage">
                  <dl className="space-y-4">
                    <Field label="Devices used" value={data.activity.devices.length ? data.activity.devices.map((d) => `${d.device_type} (last ${day(d.last_seen)})`).join(", ") : null} />
                    <Field label="Last in-app action" value={data.activity.last_action ? `${data.activity.last_action.action_name} on ${day(data.activity.last_action.action_date)}` : null} />
                    <Field label="Photos" value={`${data.media.photos.length} photo${data.media.photos.length === 1 ? "" : "s"}, ${data.media.gallery_count} in gallery`} />
                  </dl>
                </Section>
              </div>
            )}

            {tab === "history" && (
              <Section title="Admin actions on this member" action={<Link to={SITELINKS.AUDIT_LOG} className="text-xs font-semibold text-honeConnectPurple hover:underline">Full audit log</Link>}>
                {data.admin_history.length === 0 ? <p className="text-sm text-hcMuted">No admin actions recorded for this member yet.</p> : (
                  <ol className="space-y-3">{data.admin_history.map((h) => (
                    <li key={h.id} className="flex gap-3 text-sm">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-honeConnectPurple" aria-hidden />
                      <span>{h.summary}<span className="block text-xs text-hcMuted">{h.admin_name ?? "Server"} · {day(h.created_at)}</span></span>
                    </li>))}</ol>)}
              </Section>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove ${name}?`}
        message="They will lose their elite membership. You can approve them again later if needed."
        confirmLabel="Remove member"
        loading={busy}
        onConfirm={() => setStatus("REMOVED")}
        onCancel={() => setConfirmRemove(false)}
      />
      <GrantSubscriptionDialog member={grantOpen && m ? { id: m.id, name } : null} onClose={() => setGrantOpen(false)} onGranted={load} />
    </AdminLayout>
  );
};

export default ShowUser;
